import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router';
import { motion } from 'framer-motion';
import { Download, Share2, CheckCircle, Award, ArrowLeft, Loader2 } from 'lucide-react';
import Button from '../../components/ui/Button';
import { getCertificate } from '../../api/client';
import { useToast } from '../../contexts/ToastContext';

interface CertificateData {
  id: number;
  certificate_number: string;
  student_name: string;
  course_name: string;
  completion_date: string;
  issue_date: string;
  signature_url?: string;
  status: string;
}

export default function CertificateScreen(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const toast = useToast();
  const [cert, setCert] = useState<CertificateData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const res = await getCertificate(parseInt(id));
        if (res.certificate) {
          setCert(res.certificate);
        } else {
          toast.show('Certificate not found', 'error');
        }
      } catch (e: any) {
        toast.show(e.message || 'Failed to load certificate', 'error');
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="animate-spin text-slate-400" size={32} />
      </div>
    );
  }

  if (!cert) {
    return (
      <div className="min-h-screen bg-slate-50 py-12 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <Award size={48} className="mx-auto text-slate-300 mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Certificate Not Found</h1>
          <p className="text-slate-500 mb-6">The certificate you are looking for does not exist or has been revoked.</p>
          <Link to="/my-courses">
            <Button>Back to My Courses</Button>
          </Link>
        </div>
      </div>
    );
  }

  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(
    typeof window !== 'undefined' ? window.location.href : ''
  )}`;

  const handleDownload = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4 print:bg-white print:p-0">
      <div className="max-w-4xl mx-auto print:max-w-none">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 print:hidden"
        >
          <Link to="/my-courses" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to My Courses
          </Link>
        </motion.div>

        {/* Certificate */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-3xl border-4 border-amber-100 shadow-2xl overflow-hidden relative print:shadow-none print:border-4"
        >
          {/* Decorative corners */}
          <div className="absolute top-0 left-0 w-24 h-24 border-t-4 border-l-4 border-amber-500 rounded-tl-3xl" />
          <div className="absolute top-0 right-0 w-24 h-24 border-t-4 border-r-4 border-amber-500 rounded-tr-3xl" />
          <div className="absolute bottom-0 left-0 w-24 h-24 border-b-4 border-l-4 border-amber-500 rounded-bl-3xl" />
          <div className="absolute bottom-0 right-0 w-24 h-24 border-b-4 border-r-4 border-amber-500 rounded-br-3xl" />

          <div className="p-12 sm:p-16 text-center relative z-10">
            {/* Header */}
            <div className="flex items-center justify-center gap-3 mb-8">
              <div className="w-16 h-16 rounded-2xl bg-amber-500 flex items-center justify-center">
                <Award className="w-8 h-8 text-white" />
              </div>
              <div className="text-left">
                <h2 className="text-2xl font-bold text-slate-900">EcoleMinds</h2>
                <p className="text-sm text-amber-500 font-medium">Certificate of Completion</p>
              </div>
            </div>

            {/* Title */}
            <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 mb-4">
              Certificate
            </h1>
            <p className="text-lg text-slate-500 mb-10">
              This is to certify that
            </p>

            {/* Student Name */}
            <p className="text-3xl sm:text-4xl font-bold text-amber-600 mb-4">
              {cert.student_name}
            </p>

            <p className="text-lg text-slate-500 mb-6">
              has successfully completed
            </p>

            {/* Course Name */}
            <p className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">
              {cert.course_name}
            </p>
            <p className="text-base text-slate-500 mb-10">
              with distinction
            </p>

            {/* Details */}
            <div className="grid sm:grid-cols-3 gap-6 mb-10 max-w-2xl mx-auto">
              <div className="text-center">
                <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Completion Date</p>
                <p className="text-sm font-bold text-slate-900">{cert.completion_date ? new Date(cert.completion_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : '-'}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Certificate ID</p>
                <p className="text-sm font-bold text-slate-900 font-mono">{cert.certificate_number}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Issue Date</p>
                <p className="text-sm font-bold text-slate-900">{cert.issue_date ? new Date(cert.issue_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : '-'}</p>
              </div>
            </div>

            {/* QR Code */}
            <div className="flex items-center justify-center gap-6 mb-8">
              <div className="text-center">
                <img src={qrUrl} alt="Verification QR" className="w-24 h-24 mx-auto mb-2" />
                <p className="text-xs text-slate-400">Scan to verify</p>
              </div>
              <div className="text-left">
                <div className="flex items-center gap-2 text-green-600 mb-1">
                  <CheckCircle className="w-5 h-5" />
                  <span className="text-sm font-bold">Verified Certificate</span>
                </div>
                <p className="text-xs text-slate-500">
                  This certificate is authentic and<br />verifiable on our platform.
                </p>
              </div>
            </div>

            {/* Signature */}
            <div className="flex items-center justify-center gap-12">
              {cert.signature_url ? (
                <div className="text-center">
                  <img src={cert.signature_url} alt="Signature" className="h-12 mx-auto mb-2 object-contain" />
                  <p className="text-sm font-bold text-slate-900">Authorized Signatory</p>
                  <p className="text-xs text-slate-500">EcoleMinds</p>
                </div>
              ) : (
                <div className="text-center">
                  <div className="w-32 h-12 border-b border-slate-300 mb-2" />
                  <p className="text-sm font-bold text-slate-900">Authorized Signatory</p>
                  <p className="text-xs text-slate-500">EcoleMinds</p>
                </div>
              )}
              <div className="text-center">
                <div className="w-32 h-12 border-b border-slate-300 mb-2 flex items-end justify-center">
                  <span className="text-2xl font-bold text-amber-600 font-serif">EM</span>
                </div>
                <p className="text-sm font-bold text-slate-900">EcoleMinds</p>
                <p className="text-xs text-slate-500">Director</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Actions */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-8 print:hidden"
        >
          <Button size="lg" leftIcon={<Download className="w-5 h-5" />} onClick={handleDownload}>
            Print / Download PDF
          </Button>
          <Button variant="secondary" size="lg" leftIcon={<Share2 className="w-5 h-5" />} onClick={() => {
            if (navigator.share) {
              navigator.share({ title: `Certificate - ${cert.course_name}`, url: window.location.href });
            } else {
              navigator.clipboard.writeText(window.location.href);
              toast.show('Link copied to clipboard', 'success');
            }
          }}>
            Share
          </Button>
        </motion.div>
      </div>
    </div>
  );
}
